import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAggsState} from '../../contexts/AggsContext';
import {useSelectedAssetsState} from '../../contexts/SelectedAssetsContext';
import useCurrentOrg from '../helpers/useCurrentOrg';
import {contentLanguage} from '../helpers/documentLanguage';

export const visuallyHidden = {position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
    overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0};

// Mounted once above responsive panes. Facet loading and infinite scrolling do
// not change searchId; same-count searches clear then repopulate it. A live
// refresh winning a pending search still owes that search's announcement.
export default function WorkspaceStatus() {
    const {t} = useTranslation();
    const {searchId, ready, total, silentRefresh} = useAggsState();
    const {selectedAssetIds} = useSelectedAssetsState();
    const org = useCurrentOrg();
    const [resultCount, setResultCount] = useState(null);
    const [selectionCount, setSelectionCount] = useState(null);
    const previous = useRef({org: org?.id, count: selectedAssetIds.length});
    const completedSearch = useRef();

    useEffect(() => {
        if(silentRefresh) return;
        if(!ready || searchId == null) { completedSearch.current = null; setResultCount(null); return; }
        if(total == null) return;
        if(completedSearch.current === searchId) return;
        completedSearch.current = searchId;
        setResultCount(total);
    }, [searchId, ready, total, silentRefresh]);

    useEffect(() => {
        const count = selectedAssetIds.length;
        if(previous.current.org !== org?.id) setSelectionCount(null);
        else if(previous.current.count !== count) {
            setSelectionCount(count);
        }
        previous.current = {org: org?.id, count};
    }, [selectedAssetIds.length, org?.id]);

    // Translate the last announced values, never a newer silently refreshed
    // count. usedLng also identifies English fallbacks under a French page.
    const results = resultCount == null ? null : resultCount === 1 ?
        t('workspace-result-one', '1 asset found.', {returnDetails: true}) :
        t('workspace-results', '{{count}} assets found.', {count: resultCount, returnDetails: true});
    const selection = selectionCount == null ? null : selectionCount === 1 ?
        t('workspace-selected-one', '1 asset selected.', {returnDetails: true}) :
        t('workspace-selected-count', '{{count}} assets selected.', {count: selectionCount, returnDetails: true});

    return <div style={visuallyHidden}>
        <div id="workspace-results-status" role="status" aria-live="polite" aria-atomic="true" lang={contentLanguage(results?.usedLng)}>{results?.res}</div>
        <div id="workspace-selection-status" role="status" aria-live="polite" aria-atomic="true" lang={contentLanguage(selection?.usedLng)}>{selection?.res}</div>
    </div>;
}
